import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, DatePicker, Input, Tag, Alert } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { EmploymentType, UserRole } from '../../../../../types/user.types';
import dayjs from 'dayjs';
import { DepartmentDto } from '../../../../../types/department.types';
import { Store } from '../../../../../types/shop.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../../i18n/config';
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
interface WorkInfoStepProps {
  form: FormInstance;
  departments: DepartmentDto[];
  shops: Store[];
  annualLimit: number;
}

const WorkInfoStep: React.FC<WorkInfoStepProps> = ({ form, departments, shops, annualLimit }) => {
  const { t } = useTranslation();
  const [role, setRole] = useState<UserRole | undefined>(UserRole.USER);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [primaryDepartment, setPrimaryDepartment] = useState<number | undefined>(undefined);

  const pickerLocale = useMemo(() => {
    return i18n.language === 'tr' ? datePickerTr : datePickerEn;
  }, [i18n.language]);

  useEffect(() => {
    setRole(form.getFieldValue('role') || UserRole.USER);
    const deptIds = form.getFieldValue('departmentId') || [];
    const primaryDeptId = form.getFieldValue('primaryDepartmentId');
    setSelectedDepartments(deptIds);
    setPrimaryDepartment(primaryDeptId);
  }, [form.getFieldValue('role'), form.getFieldValue('departmentId'), form.getFieldValue('primaryDepartmentId')]);

  // Departman seçimi değiştiğinde ana departmanı kontrol et
  const handleDepartmentChange = (values: number[]) => {
    setSelectedDepartments(values);

    // Eğer ana departman seçili değilse veya seçili departmanlar arasında yoksa temizle
    if (primaryDepartment && !values.includes(primaryDepartment)) {
      setPrimaryDepartment(undefined);
      form.setFieldValue('primaryDepartmentId', undefined);
    }

    // Eğer sadece bir departman seçiliyse otomatik olarak ana departman yap
    if (values.length === 1) {
      setPrimaryDepartment(values[0]);
      form.setFieldValue('primaryDepartmentId', values[0]);
    }
  };

  const handlePrimaryDepartmentChange = (value: number) => {
    setPrimaryDepartment(value);
  };

  // Ana departman seçimi için mevcut departmanları filtrele
  const primaryDepartmentOptions = departments
    .filter(dept => selectedDepartments.includes(dept.id))
    .map(dept => ({
      value: dept.id,
      label: dept.name
    }));

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label">{t('employees.modals.role')}</label>
          <Form.Item
            name="role"
            rules={[{ required: true, message: t('employees.validationErrors.role') }]}
          >
            <Select
              placeholder={t('employees.modals.role')}
              options={[
                { value: UserRole.ADMIN, label: t('employees.modals.admin') },
                { value: UserRole.USER, label: t('employees.modals.employee') },
                { value: UserRole.MANAGER, label: t('employees.modals.manager') }
              ]}
              onChange={(value) => setRole(value)}
            />
          </Form.Item>
        </div>
      </div>

      {!(role === UserRole.ADMIN) && (
        <>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('employees.modals.department')}</label>
              <Form.Item
                name="departmentId"
                rules={[{ required: true, message: t('employees.validationErrors.department') }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t('employees.modals.department')}
                  options={departments?.map(dept => ({
                    value: dept.id,
                    label: dept.name
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  tagRender={(props) => {
                    return (
                      <Tag color="#7338A0" closable onClose={props.onClose} style={{ textAlign: 'center', justifyContent: 'center', alignItems: 'center', display: 'flex', flexWrap: 'nowrap' }}>
                        {props.label}
                      </Tag>
                    );
                  }}
                  onChange={handleDepartmentChange}
                />
              </Form.Item>
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('employees.modals.branch')}</label>
              <Form.Item
                name="branchId"
                rules={[{ required: true, message: t('employees.validationErrors.branch') }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t('employees.modals.branch')}
                  options={shops.map(shop => ({
                    value: shop.id,
                    label: shop.name
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  tagRender={(props) => {
                    return (
                      <Tag color="#7338A0" closable onClose={props.onClose} style={{ textAlign: 'center', justifyContent: 'center', alignItems: 'center', display: 'flex', flexWrap: 'nowrap' }}>
                        {props.label}
                      </Tag>
                    );
                  }}
                />
              </Form.Item>
            </div>
          </div>
          {/* Ana Departman Seçimi */}
          {selectedDepartments.length > 0 && (
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">
                  Ana Departman <span className="text-danger">*</span>
                </label>
                <Form.Item
                  name="primaryDepartmentId"
                  rules={[{ required: true, message: 'Ana departman seçimi zorunludur' }]}
                >
                  <Select
                    placeholder="Ana departman seçiniz"
                    options={primaryDepartmentOptions}
                    onChange={handlePrimaryDepartmentChange}
                    disabled={selectedDepartments.length === 0}
                  />
                </Form.Item>
                <small className="text-muted">
                  <i className="ti ti-info-circle me-1"></i>
                  Çalışan ana departmanına ait çalışma günü ve çalışma saatlerine tabi tutulacaktır.
                </small>
              </div>
            </div>
          )}

          {/* Departman seçimi yapılmadıysa uyarı */}
          {selectedDepartments.length === 0 && (
            <div className="col-12">
              <Alert
                message="Önce departman seçimi yapınız"
                description="Ana departman seçebilmek için öncelikle en az bir departman seçmelisiniz."
                type="info"
                showIcon
                className="mb-3"
              />
            </div>
          )}


        </>
      )}


      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label">{t('employees.modals.annualLeave', { default: annualLimit })}</label>
          <Form.Item
            name="annualLeaveLimit"
          >
            <Input />
          </Form.Item>
        </div>
      </div>

      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label">{t('employees.modals.workType')}</label>
          <Form.Item
            name="isFixedShift"
            rules={[{ required: true, message: t('employees.validationErrors.workType') }]}
          >
            <Select
              options={[
                { value: true, label: t('employees.modals.fixedShift') },
                { value: false, label: t('employees.modals.shiftBased') },
              ]}
            />
          </Form.Item>
        </div>
      </div>
      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label">{t('employees.modals.hireDate')}</label>
          <Form.Item
            name="hireDate"
            rules={[{ required: true, message: t('employees.validationErrors.hireDate') }]}
            getValueProps={(i) => ({
              value: i ? dayjs(i) : null
            })}
            getValueFromEvent={(date) => date ? date.format('YYYY-MM-DD') : null}

          >
            <DatePicker
              className="form-control"
              format="YYYY-MM-DD"
              placeholder={t('employees.modals.selectHireDate')}
              locale={pickerLocale}
            />
          </Form.Item>
        </div>
      </div>
      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label">İşten Çıkış Tarihi</label>
          <Form.Item
            name="terminationDate"
            getValueProps={(i) => ({
              value: i ? dayjs(i) : null
            })}
            getValueFromEvent={(date) => date ? date.format('YYYY-MM-DD') : null}
          >
            <DatePicker
              className="form-control"
              format="YYYY-MM-DD"
              placeholder="İşten çıkış tarihi"
              locale={pickerLocale}
            />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export default WorkInfoStep; 